import React from 'react';
import { ArrowRight, CheckCircle, Users, TrendingUp, Globe } from 'lucide-react';

interface CTASectionProps {
  onOpenAccountClick?: () => void;
  onScheduleDemoClick?: () => void;
  onSignupClick?: () => void;
}

const benefits = [
  'No setup fees',
  'Onboard in under 10 minutes',
  'Dedicated support team',
  'Cancel anytime',
];

const socialProofStats = [
  { icon: <Users size={16} />, value: '500+', label: 'Active businesses' },
  { icon: <TrendingUp size={16} />, value: '$60M+', label: 'All time processed' },
  { icon: <Globe size={16} />, value: '50+', label: 'Countries' },
];

const CTASection: React.FC<CTASectionProps> = ({ onOpenAccountClick, onScheduleDemoClick }) => {
  return (
    <section className="py-16 lg:py-28 bg-transparent">
      <div className="section-container">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-fintech-primary/86 via-blue-800/78 to-slate-900/74 border border-white/20 backdrop-blur-sm px-6 py-12 sm:px-16 sm:py-16">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-blue-400/15 blur-3xl" />
            {/* Grid pattern */}
            <div
              className="absolute inset-0 opacity-5"
              style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          <div className="relative z-10 public-readable-panel public-readable-panel--dark public-readable-panel--wide">
            {/* Social proof stats */}
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-8 sm:mb-10">
              {socialProofStats.map((stat, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm">
                  <span className="text-fintech-accent scale-90 sm:scale-100">{stat.icon}</span>
                  <div>
                    <p className="text-white font-bold text-xs sm:text-sm leading-none">{stat.value}</p>
                    <p className="text-blue-300 text-[10px] sm:text-xs调节-none mt-0.5">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Badge */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-blue-100 text-xs font-semibold uppercase tracking-wider">
                Start Today — It's Free
              </div>
            </div>

            {/* Headline */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight max-w-2xl mx-auto text-center">
              Ready to Streamline Your Procurement?
            </h2>

            {/* Subtext */}
            <p className="text-blue-200 text-lg mb-10 max-w-xl mx-auto leading-relaxed text-center">
              Join hundreds of businesses already using Kenluk Pay for seamless global vendor management.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <button
                className="btn-primary-large bg-white text-fintech-primary hover:bg-blue-50 w-full sm:w-auto"
                onClick={onOpenAccountClick}
              >
                Open Free Account
                <ArrowRight size={18} />
              </button>
              <button
                className="btn-secondary-large w-full sm:w-auto"
                style={{ backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
                onClick={onScheduleDemoClick}
              >
                Schedule a Demo
              </button>
            </div>

            {/* Benefits list */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-1.5 text-blue-200 text-sm">
                  <CheckCircle size={14} className="text-fintech-accent flex-shrink-0" />
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
